import ReactECharts from "echarts-for-react";
import { T, TIERS, MATURITY_LEVELS } from "../lib/model";

export function ScoreGauge({ score, qualitative = false }) {
  const option = {
    animation: true,
    animationDuration: 1200,
    animationEasing: 'cubicOut',
    series: [
      {
        type: "gauge",
        startAngle: 210,
        endAngle: -30,
        min: 0,
        max: 100,
        splitNumber: 10,
        radius: '95%',
        axisLine: {
          lineStyle: {
            width: 18,
            color: [
              [0.24, T.limeDeep],
              [0.49, T.gold],
              [0.74, T.orange],
              [1, T.danger],
            ],
            shadowBlur: 10,
            shadowColor: 'rgba(0,0,0,0.1)'
          },
        },
        pointer: { 
          icon: 'path://M12.8,0.7l12,40.1H0.7L12.8,0.7z',
          length: '75%',
          width: 8,
          offsetCenter: [0, '5%'],
          itemStyle: {
            color: T.ink,
            shadowBlur: 5,
            shadowColor: 'rgba(0,0,0,0.2)'
          }
        },
        anchor: {
          show: true,
          showAbove: true,
          size: 14,
          itemStyle: {
            color: T.ink,
            borderWidth: 2,
            borderColor: '#fff'
          }
        },
        axisTick: { show: false },
        splitLine: { 
          show: true,
          length: 5,
          lineStyle: { color: '#fff', width: 2 }
        },
        axisLabel: { 
          show: false // Hide numeric axis labels for qualitative view
        },
        detail: {
          show: !qualitative, // Hide number in qualitative mode
          formatter: "{value}",
          color: T.ink,
          fontSize: 48,
          fontWeight: 900,
          offsetCenter: [0, "65%"],
        },
        data: [{ value: score ?? 0 }],
      },
    ],
  };
  
  return (
    <div className="w-full flex justify-center" style={{ height: 260 }}>
      <ReactECharts 
        option={option} 
        style={{ height: "100%", width: "100%", maxWidth: "350px" }} 
        notMerge={true}
        lazyUpdate={true}
      />
    </div>
  );
}

export function MaturityMeter({ level }) {
  const activeLevel = MATURITY_LEVELS.find(l => l.level === level) || MATURITY_LEVELS[0];
  
  return (
    <div className="w-full">
      <div className="flex justify-between items-end mb-4">
        <div>
          <div className="text-[10px] font-black uppercase text-gray-400 tracking-widest mb-1">Maturity Level</div>
          <div className="text-xl font-black text-[#0D2440]">{activeLevel.name}</div>
        </div>
        <div className="text-3xl font-black text-[#0D2440]">{level}<span className="text-gray-300 text-lg">/5</span></div>
      </div>
      <div className="flex gap-1.5 h-3">
        {[1, 2, 3, 4, 5].map(l => (
          <div 
            key={l}
            className={`flex-1 rounded-full transition-all duration-700 ${l <= level ? 'bg-teal-500 shadow-[0_0_10px_rgba(20,174,198,0.3)]' : 'bg-gray-100'}`}
          />
        ))}
      </div>
      <p className="mt-4 text-[11px] text-gray-500 font-bold leading-relaxed">{activeLevel.description}</p>
    </div>
  );
}

export function ConfidenceIndicator({ pct, notice }) {
  return (
    <div className="w-full">
      <div className="flex justify-between items-end mb-4">
        <div>
          <div className="text-[10px] font-black uppercase text-gray-400 tracking-widest mb-1">Result Confidence</div>
          <div className="text-xl font-black text-[#0D2440]">{notice || (pct >= 80 ? 'Validated' : pct >= 50 ? 'Evidence-Supported' : 'Self-Reported')}</div>
        </div>
      </div>
      <div className="h-3 bg-gray-100 rounded-full overflow-hidden">
        <div 
          className="h-full bg-blue-500 transition-all duration-1000 ease-out"
          style={{ width: `${pct}%` }}
        />
      </div>
      <p className="mt-4 text-[11px] text-gray-500 font-bold leading-relaxed italic">
        Reliability based on answer completeness and consistency.
      </p>
    </div>
  );
}

export function DimensionRadar({ dims }) {
  const option = {
    animation: true,
    animationDuration: 1200,
    radar: {
      indicator: (dims ?? []).map((d) => ({ 
        name: d.name, 
        max: 100 
      })),
      radius: "28%",
      center: ['50%', '50%'],
      axisName: { 
        color: T.slate, 
        fontSize: 9,
        fontWeight: '900',
        lineHeight: 11,
        padding: [0, 5],
        formatter: (value) => {
          if (value === "Phishing and Social Engineering") return "Phishing\n&\nSocial Eng";
          if (value === "Identity and Credential Exposure") return "Identity\n&\nCredentials";
          if (value === "Business Process Manipulation") return "Business\nProcess";
          if (value === "Sensitive Data Behavior") return "Data\nBehavior";
          if (value === "AI and Shadow Technology") return "AI &\nShadow Tech";
          if (value === "Workforce Lifecycle") return "Workforce\nLifecycle";
          if (value === "Leadership and Reporting Culture") return "Leadership\n&\nCulture";
          return value.split(' ').join('\n');
        }
      },
      splitArea: { 
        areaStyle: { 
          color: ["#F8FAFC", "#F1F5F9"],
          shadowBlur: 10,
          shadowColor: 'rgba(0,0,0,0.02)'
        } 
      },
      splitLine: { lineStyle: { color: T.rule, width: 1 } },
      axisLine: { lineStyle: { color: T.rule, width: 1 } },
      axisNameGap: 12,
    },
    series: [
      {
        type: "radar",
        symbol: 'circle',
        symbolSize: 6,
        data: [
          {
            value: (dims ?? []).map((d) => (d.pct === null ? 0 : Math.round(d.pct * 100))),
            name: "Risk profile",
            areaStyle: { 
              color: {
                type: 'radial',
                x: 0.5, y: 0.5, r: 0.5,
                colorStops: [
                  { offset: 0, color: 'rgba(21, 90, 156, 0.4)' },
                  { offset: 1, color: 'rgba(21, 90, 156, 0.1)' }
                ]
              }
            },
            lineStyle: { color: T.navy, width: 3 },
            itemStyle: { color: T.navy, borderColor: '#fff', borderWidth: 2 },
          },
        ],
      },
    ],
  };

  return (
    <div className="w-full flex justify-center" style={{ height: 320 }}>
      <ReactECharts 
        option={option} 
        style={{ height: "100%", width: "100%" }}
        notMerge={true}
        lazyUpdate={true}
      />
    </div>
  );
}

export function TierScale({ score }) {
  const safeScore = score ?? 0;
  return (
    <div className="mt-8 px-4 w-full max-w-sm mx-auto">
      <div className="relative h-6 bg-gray-100 rounded-full flex items-center p-1 shadow-inner overflow-hidden">
        {TIERS.map((t, i) => (
          <div 
            key={t.name}
            style={{ 
              width: i === 0 ? `${t.max}%` : `${t.max - TIERS[i-1].max}%`,
              background: t.color,
              opacity: 0.25
            }}
            className="h-full"
          />
        ))}
        <div 
          className="absolute top-0 h-full w-2 bg-white shadow-[0_0_10px_rgba(0,0,0,0.3)] transition-all duration-1000 ease-out z-10 border-x border-gray-200"
          style={{ left: `${Math.min(Math.max(0, safeScore), 100)}%`, transform: 'translateX(-50%)' }}
        />
      </div>
      <div className="flex justify-between mt-4">
        {TIERS.map(t => (
          <div key={t.name} className="flex flex-col items-center">
            <div className="w-2.5 h-2.5 rounded-full mb-1.5 shadow-sm" style={{ background: t.color }} />
            <span className="text-[9px] font-black text-gray-400 uppercase tracking-widest">{t.name}</span>
          </div>
        ))}
      </div>
    </div>
  );
}